import React, { useEffect, useState } from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import Hero from '@src/components/VsCompetition/Hero'
import ComparisonTable from '@src/components/VsCompetition/ComparisonTable'
import QuoteModal from '@src/components/VsCompetition/QuoteModal'
import { COMPETITOR_PARAM, resolveCompetitor } from '@src/components/VsCompetition/competitors'
import { ClosingCta, Faq, Reasons, Streams } from '@src/components/VsCompetition/Sections'
import { segmentTrack } from '@src/functions/segment'
import classes from '@src/styles/vs-competition.module.css'
// eslint-disable-next-line import/extensions
import content from '../../json/dimension-vs-competition-data.json'

const CANONICAL = 'https://getdimension.com/dimension-vs-competition'

const META_DESCRIPTION =
  'Compare Dimension to Safety-Kleen, Clean Harbors, Veolia and seven other ' +
  'hazardous waste providers — coverage, pricing and how long each takes to quote. ' +
  'Instant online pricing, typically 30% less.'

const breadcrumbs = {
  items: [
    { name: 'Home', url: 'https://getdimension.com' },
    { name: 'Hazardous Waste Disposal', url: 'https://getdimension.com/hazardous-waste-disposal' },
    { name: 'Dimension vs. the Competition', url: CANONICAL },
  ],
}

const faqData = {
  questions: content.faq.questions.map(({ question, answer }) => ({ question, answer })),
}

const serviceData = {
  name: 'Hazardous Waste Disposal',
  description: META_DESCRIPTION,
  serviceType: 'Hazardous Waste Disposal',
  url: CANONICAL,
}

/**
 * Set on <html> before first paint when the URL carries `?competitor=`, and cleared
 * once the router has the query. The page is statically rendered, so the server can
 * only ever emit the first rotation name; without this a visitor from a Clean Harbors
 * ad would see "Leaving Safety‑Kleen?" flash before their competitor swaps in. The
 * CSS reveals the name on its own after a moment, so a page whose JS never loads
 * still shows a complete heading. See `.swap` in vs-competition.module.css.
 */
const PIN_PENDING_CLASS = 'vs-pin-pending'

const PIN_PENDING_SCRIPT = `if(/[?&]${COMPETITOR_PARAM}=/.test(location.search))document.documentElement.classList.add('${PIN_PENDING_CLASS}')`

function DimensionVsCompetition() {
  // Both "Get a quote" buttons open the same modal, so the state lives here rather
  // than in either section.
  const [quoteOpen, setQuoteOpen] = useState(false)

  // Competitor-specific ads link here with `?competitor=<slug>` to pin the heading.
  // The query is empty until the router is ready (static page), hence the gate.
  const router = useRouter()
  const pinned = router.isReady ? resolveCompetitor(router.query[COMPETITOR_PARAM]) : null

  useEffect(() => {
    if (!router.isReady) return undefined
    document.documentElement.classList.remove(PIN_PENDING_CLASS)
    return () => document.documentElement.classList.remove(PIN_PENDING_CLASS)
  }, [router.isReady])

  const openQuote = (placement: string) => () => {
    segmentTrack('Vs Competition - Quote Modal Opened', {
      page: 'Vs Competition',
      placement,
      competitor: pinned?.slug ?? null,
    })
    setQuoteOpen(true)
  }

  return (
    <>
      <Head>
        <title>Dimension vs. Safety-Kleen, Clean Harbors &amp; 8 More | Compare Providers</title>
        <meta name='description' content={META_DESCRIPTION} />
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        <link rel='canonical' href={CANONICAL} />
        <SchemaMarkup type='Service' data={serviceData} />
        <SchemaMarkup type='FAQPage' data={faqData} />
        <SchemaMarkup type='LocalBusiness' />
        <SchemaMarkup type='BreadcrumbList' data={breadcrumbs} />
      </Head>
      <div className={classes.root}>
        {/* Runs during HTML parse, ahead of the heading — see PIN_PENDING_CLASS. */}
        {/* eslint-disable-next-line react/no-danger */}
        <script dangerouslySetInnerHTML={{ __html: PIN_PENDING_SCRIPT }} />
        <Hero {...content.hero} pinned={pinned?.name} onQuoteClick={openQuote('hero')} />
        {/* The service pages' "Proudly Serving" block. */}
        <Clients />
        <ComparisonTable {...content.comparison} />
        <Reasons {...content.reasons} />
        <Streams {...content.streams} />
        <Faq {...content.faq} />
        <ClosingCta
          heading={content.closing.heading}
          ctaText={content.closing.ctaText}
          ctaLink={content.hero.ctaLink}
          onQuoteClick={openQuote('closing')}
        />
        <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} />
      </div>
      <RelatedServices
        services={[
          {
            label: 'Hazardous Waste Disposal',
            href: '/hazardous-waste-disposal',
            description:
              'Safe disposal of chemical waste, solvents, and other hazardous materials for businesses.',
          },
          {
            label: 'Lab Pack Services',
            href: '/lab-pack-services',
            description:
              'Packing, manifesting and disposal of mixed laboratory chemicals by trained chemists.',
          },
          {
            label: 'Oil Recycling',
            href: '/oil-recycling',
            description:
              'Used cooking, motor and hydraulic oil pickup for businesses, with instant online pricing.',
          },
          {
            label: 'Business Waste Solutions',
            href: '/b2b-solutions',
            description:
              'Customized waste management for enterprises with dedicated reporting and account management.',
          },
        ]}
      />
    </>
  )
}

export default DimensionVsCompetition
